import React from "react";
import "../styles/custom-modal.css";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void; // Action to trigger on confirmation
  title: string;
  message: string;
  type: "success" | "error" | "loading" | "confirmation" | "warning"; // Added "confirmation" type
  allowClose?: boolean; // New prop to control close functionality
}

const CustomModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type,
  allowClose = true, // Default to true
}) => {
  if (!isOpen) return null;

  // Handle overlay click - only close if allowClose is true
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && allowClose) {
      onClose();
    }
  };

  let icon;
  switch (type) {
    case "success":
      icon = (
        <div className="cross-container">
          <div className="checkmark-circle"></div>
          <svg className="checkmark" viewBox="0 0 52 52">
            <path fill="none" stroke="#fff" strokeWidth="5" d="M14 27l7 7 16-16" />
          </svg>
        </div>
      );
      break;
    case "error":
      icon = (
        <div className="cross-container">
          <div className="cross-circle"></div>
          <svg className="cross" viewBox="0 0 52 52">
            <line x1="15" y1="15" x2="37" y2="37" stroke="#fff" strokeWidth="5" />
            <line x1="37" y1="15" x2="15" y2="37" stroke="#fff" strokeWidth="5" />
          </svg>
        </div>
      );
      break;
      case "warning":
        icon = (
          <div className="cross-container">
            <div className="warning-circle"></div>
            <svg className="warning" viewBox="0 0 52 52">
              {/* Exclamation mark instead of X */}
              <line x1="26" y1="13" x2="26" y2="32" stroke="#fff" strokeWidth="5" strokeLinecap="round" />
              <circle cx="26" cy="40" r="3" fill="#fff" />
            </svg>
          </div>
        );
        break;
    case "loading":
      icon = <div className="load-container"><div className="loader"></div></div>;
      break;
    case "confirmation":
      icon = null; // No icon for confirmation, only the message and buttons
      break;
    default:
      icon = null;
  }

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal">
        {/* Only show close button if allowClose is true */}
        {allowClose && (
          <span className="close-btn" onClick={(e) => { e.stopPropagation(); onClose(); }}>&times;</span>
        )}
        <div className="modal-icon">{icon}</div>
        <h2 className={`modal-title ${type === "confirmation" ? "text-left" : ""}`}>{title}</h2>

        <hr></hr>
        <p className={`modal-message ${type === "confirmation" ? "text-left" : ""}`}>{message}</p>

        {/* Conditional rendering for confirmation buttons */}
        {type === "confirmation" ? (
          <div className="flex justify-end space-x-4 pt-5">
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                onClose(); // Close the modal on cancel
              }}
              className="bg-gray-300 px-4 py-2 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                if (onConfirm) onConfirm(); // Call the onConfirm action when confirmed
              }}
              className="save-button px-4 py-2 rounded-md"
            >
              Confirm
            </button>
          </div>
        ) : (
          type !== "loading" && allowClose && (
            <div className="flex justify-end space-x-4 ">
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent event bubbling
                  onClose();
                }}
                className="bg-gray-300 px-4 py-2 rounded-md"
              >
                Close
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default CustomModal;